import React, { BaseSyntheticEvent, useEffect, useState } from 'react'
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonRow,
  IonToggle,
} from '@ionic/react'
import { addCircleOutline, trash, trashOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  savePatientDemographics,
  addPhone,
  setPhoneDetailsPNNV,
  setPhoneDetails,
  setPhoneDetailsPNNVToggle,
} from '../store/patientDemographics.actions'
import { deletePhone, setPhoneNumber, setPhoneType } from '../../options/store/options.actions'
import { IPhoneDetail } from '../store/patientDemographics.types'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import { OptionsConstants } from '../../options/store/options.constants'
import { transformPhoneNumber } from '../../../utilities'
import Validation from '../../../components/validation/Validation.component'
import validationService from '../../validation/services/validation.services'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { getAllPNNVOptionsSelector, getPatientDemographicsOptionsSelector } from '../../options/selectors'
import { getPatientDemographicsSelector } from '../selectors'
import { IOption, IPNNVOption } from '../../../common/common.types'
import { IAlertState } from '../../../components/alert/store/alert.types'
import { showAlert } from '../../vitals/store/vitals.actions'
import PNNVList from '../../../components/pnnvList/PNNVList.component'
import { phoneDetailsInit } from '../store/patientDemographics.reducer'

const PhoneDetails: React.FC = () => {
  const { t } = useTranslation()
  const patientDemographicsOptions = useSelector(getPatientDemographicsOptionsSelector)
  const { phoneTypeOptions } = patientDemographicsOptions
  const getPatientDemographics = useSelector(getPatientDemographicsSelector)
  const { phoneDetails, phoneDetailsPNNV, __phoneDetailsPNNVToggle } = getPatientDemographics
  const dispatch = useDispatch()
  const [flag, setFlag] = useState(true)
  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const pnnvOptions = allPNNVOptions?.['ePatient.18']

  const setPhoneNumberOnChange = (numbers: string[], phoneDetail: IPhoneDetail) => {
    const newPhoneNumber = transformPhoneNumber(numbers.join(''))
    dispatch(setPhoneNumber(newPhoneNumber, phoneDetail))
    save()
  }

  const setTypeOnChange = (option: IOption, phoneDetail: IPhoneDetail) => {
    dispatch(setPhoneType(option, phoneDetail))
    save()
  }

  const deletePhoneDetail = (phoneDetail: IPhoneDetail) => {
    dispatch(deletePhone(phoneDetail))
    save()

    validationService.clearValidation(OptionsConstants.SET_PHONE, phoneDetail.key)
  }

  const addPhoneDetail = () => {
    dispatch(addPhone())
    save()
  }

  const save = () => {
    dispatch(savePatientDemographics())
  }

  useEffect(() => {
    if (flag) {
      phoneDetails.map((phoneDetail: IPhoneDetail) => {
        phoneDetail.number && dispatch(setPhoneNumber(phoneDetail.number, phoneDetail))
      })
    }
  }, [flag])

  useEffect(() => {
    setFlag(true)

    return () => {
      setFlag(false)
    }
  }, [])

  const handlePNNVToggle = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    const isChecked = e.target.checked
    if (isChecked && phoneDetails && phoneDetails.length > 0) {
      const changeSectionWarningAlert: IAlertState = {
        header: t('patientDemographics.PatientPhoneInformation'),
        message: t('pnnv.userConfirmationMessage'),
        buttons: [
          { text: t('pnnv.cancel') },
          {
            text: t('pnnv.continueAndClear'),
            role: 'destructive',
            handler: () => handleClearPreviousSelection(isChecked),
          },
        ],
      }
      dispatch(showAlert(changeSectionWarningAlert))
      dispatch(setPhoneDetailsPNNVToggle(!isChecked))
      save()
    } else {
      handleClearPreviousSelection(isChecked)
    }
  }

  const handleClearPreviousSelection = (pnnvChecked: boolean) => {
    if (pnnvChecked) {
      const defaultPhoneDetails = [
        {
          ...phoneDetailsInit(),
          type: phoneTypeOptions.find((type) => Number(type.id) === 1) || null,
        },
      ]
      dispatch(setPhoneDetails(defaultPhoneDetails))
      const phoneDetailPnnvOption = pnnvOptions?.length > 0 ? pnnvOptions[0] : null
      if (phoneDetailPnnvOption) {
        const phoneDetailPnnv = {
          id: phoneDetailPnnvOption.id,
          name: phoneDetailPnnvOption.name,
          pn: phoneDetailPnnvOption.isPN ? phoneDetailPnnvOption.nemsisCode : '',
          nv: phoneDetailPnnvOption.isNV ? phoneDetailPnnvOption.nemsisCode : '',
        }
        dispatch(setPhoneDetailsPNNV(phoneDetailPnnv))
      }
    } else {
      dispatch(setPhoneDetailsPNNV(null))
    }
    dispatch(setPhoneDetailsPNNVToggle(pnnvChecked))
    save()
  }

  const selectOption = (option: IPNNVOption | null) => {
    dispatch(setPhoneDetailsPNNV(option as IPNNVOption))
    save()
  }

  return (
    <IonList>
      <IonItemDivider color='light' className='ion-padding-start ion-padding-top'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('patientDemographics.PatientPhoneInformation')}
        </IonLabel>
        <IonItem key='Toggle_PatientMedicationPNNVToggle' slot='end' lines='none' color='none'>
          <IonLabel>{t('patientMedications.PNNVToggle')}</IonLabel>
          <IonToggle
            slot='start'
            name='phoneDetailsPNNVToggle'
            checked={__phoneDetailsPNNVToggle}
            className='extra-padding'
            onClick={(e) => handlePNNVToggle(e)}
            color='zoll-blue-secondary'
          />
        </IonItem>
      </IonItemDivider>
      {!__phoneDetailsPNNVToggle ? (
        <>
          {phoneDetails.map((phoneDetail: IPhoneDetail, index: number) => (
            <IonItemSliding key={phoneDetail.key}>
              <IonCard className='ion-no-margin ion-margin-vertical'>
                <IonCardContent>
                  <IonGrid className='no-padding'>
                    <IonRow>
                      <IonCol>
                        {t('patientDemographics.addressInfo.Phone')} {index + 1}
                      </IonCol>
                      {!phoneDetail.readonly && (
                        <IonButtons>
                          <IonButton data-testid='delete' onClick={() => deletePhoneDetail(phoneDetail)}>
                            <IonIcon icon={trashOutline} />
                          </IonButton>
                        </IonButtons>
                      )}
                    </IonRow>

                    <IonRow>
                      <IonCol className='picklist'>
                        <NumberPad
                          isPHI
                          disabled={phoneDetail.readonly}
                          elementId={`PhoneNumber${index}`}
                          dataTestId={`PhoneNumber${index}`}
                          values={phoneDetail.number ? phoneDetail.number.split('') : []}
                          label={t('patientDemographics.addressInfo.Number')}
                          onNumPadClick={(numbers) => {
                            setPhoneNumberOnChange(numbers, phoneDetail)
                          }}
                        />
                        <Validation id={OptionsConstants.SET_PHONE} cardinalityKey={phoneDetail.key} />
                      </IonCol>
                    </IonRow>

                    <hr className='field-divider' />

                    <IonRow>
                      <IonCol className='picklist'>
                        <SingleSelect
                          disabled={phoneDetail.readonly}
                          dataTestID='PhoneType'
                          selectedOption={phoneDetail.type}
                          label={t('patientDemographics.addressInfo.Type')}
                          listHeader={t('patientDemographics.addressInfo.Type')}
                          options={phoneTypeOptions}
                          dispatchData={phoneDetail}
                          action={setTypeOnChange}
                        />
                      </IonCol>
                    </IonRow>

                    <hr className='field-divider' />
                  </IonGrid>
                </IonCardContent>
              </IonCard>
              <IonItemOptions side='end' className='delete-swipe'>
                <IonItemOption
                  color='danger'
                  onDrag={() => deletePhoneDetail(phoneDetail)}
                  onClick={() => deletePhoneDetail(phoneDetail)}>
                  <IonIcon slot='icon-only' icon={trash} />
                </IonItemOption>
              </IonItemOptions>
            </IonItemSliding>
          ))}
          <IonRow className='ion-float-right'>
            <IonCol>
              <IonItem lines='none'>
                <IonButton
                  data-testid='AddPhone'
                  className='transform-none multi-card-add-button'
                  fill='clear'
                  color='primary-alt'
                  onClick={addPhoneDetail}>
                  <IonIcon icon={addCircleOutline} />
                  <IonLabel>{t('patientDemographics.buttons.AddPhone')}</IonLabel>
                </IonButton>
              </IonItem>
            </IonCol>
          </IonRow>
        </>
      ) : (
        <>
          <PNNVList options={pnnvOptions} selectedOption={phoneDetailsPNNV || null} onSelection={selectOption} />
        </>
      )}
    </IonList>
  )
}

export default PhoneDetails
